import { useMutation, useQueryClient } from "@tanstack/react-query";

import { useCurrentCompanyId } from "@/hooks/useCurrentCompanyId";
import { useCurrentJobId } from "@/hooks/useCurrentJobId";
import { candidacyInfoQueryKey } from "@/hooks/queries/useGetCandidacyInfo";
import { useRefetchCandidaciesMinimal } from "@/hooks/refetches/useRefetchCandidaciesMinimal";
import { guacHttpClient } from "@/lib/http";
import { HttpResponse, StevieRouteMode } from "@/types";

// Routes a candidacy through Stevie by hand: a text from the staffer's own line,
// plus Stevie's own first text when the candidate has never been contacted.
export function useRouteCandidacyThroughStevie() {
  const currentCompanyId = useCurrentCompanyId();
  const currentJobId = useCurrentJobId();
  const queryClient = useQueryClient();
  const refetchCandidaciesMinimal = useRefetchCandidaciesMinimal();

  return useMutation({
    mutationFn: async ({
      candidacyId,
      infoToGather,
    }: {
      candidacyId: string;
      // What the staffer wants Stevie to dig into on the call.
      infoToGather?: string;
    }) => {
      const res = await guacHttpClient.post<
        { mode: StevieRouteMode },
        HttpResponse<{ mode: StevieRouteMode }>
      >("text-ai-outreaches/route-through-stevie", {
        candidacyId,
        infoToGather,
      });
      return res.data;
    },
    onSuccess: (_, { candidacyId }) => {
      // The route is one-shot and shows in the pipeline, so both the sheet's
      // own payload and the candidacy list have to pick up the nudge.
      void queryClient.invalidateQueries({
        queryKey: candidacyInfoQueryKey(
          currentCompanyId,
          currentJobId,
          candidacyId,
        ),
      });
      refetchCandidaciesMinimal();
    },
  });
}
